import "../css/app.css";
import "./bootstrap";

import { createInertiaApp } from "@inertiajs/react";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import axios from "axios";

const appName = import.meta.env.VITE_APP_NAME || "Sav-Rx Passport";

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
axios.defaults.headers.common["X-User-Timezone"] = userTimeZone;

createInertiaApp({
    title: (title) => `${appName} - ${title}`,
    resolve: async (name) => {
        // Import all possible pages
        const pages = import.meta.glob<{ default: React.ComponentType<any> }>([
            "./Core/Pages/**/*.tsx",
            "./Portals/Passport/Pages/**/*.tsx",
            "./Portals/Passport/Pages/Admin/**/*.tsx",
            "./Portals/Passport/Pages/Auth/**/*.tsx",
            "./Portals/Patient/Pages/**/*.tsx",
            "./Portals/Provider/Pages/**/*.tsx",
            "./Portals/Admin/Pages/**/*.tsx",
            "./Portals/RxDC/Pages/**/*.tsx",
        ]);

        // Normalize file paths (key = normalized path, value = resolver)
        const normalizedPages: Record<
            string,
            () => Promise<{ default: React.ComponentType<any> }>
        > = {};

        for (const [path, resolver] of Object.entries(pages)) {
            const normalizedPath = path.replace(/^\.\/|\.tsx$/g, ""); // remove ./ and .tsx
            normalizedPages[normalizedPath] = resolver;
        }

        if (name.startsWith("Core/")) {
            const corePage = normalizedPages[name];
            if (corePage) {
                const module = await corePage();
                return module.default;
            }
        }

        // For some reason, `Pages/` sometimes get added to the start of the name
        if (name.startsWith("Pages/")) {
            name = name.replace("Pages/", "Portals/");
        }

        const portalPath = `Portals/${name}`;
        const portalPage = normalizedPages[portalPath];

        if (portalPage) {
            const module = await portalPage();
            return module.default;
        }

        throw new Error(`Page not found: ${name}`);
    },

    setup({ el, App, props }) {
        Sentry.init({
            dsn: import.meta.env.VITE_DSN,
            release: import.meta.env.VITE_SENTRY_RELEASE,

            integrations: [
                Sentry.feedbackIntegration({
                    isEmailRequired: true,
                    isNameRequired: true,
                    colorScheme: "system",
                    autoInject: false,
                }),
                Sentry.replayIntegration(),
            ],
            replaysSessionSampleRate:
                parseFloat(import.meta.env.VITE_REPLAY_SESSION_SAMPLE_RATE) ||
                0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate:
                parseFloat(import.meta.env.VITE_REPLAY_ON_ERROR_SAMPLE_RATE) ||
                1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });
        const root = createRoot(el);

        root.render(<App {...props} />);
    },
    progress: {
        color: "#4B5563",
    },
});
